import { useCallback, useEffect, useRef, useState } from 'react';
import {
  MESSAGE_FROM,
  MESSAGE_TYPES,
  NUDGYT_AGENT_MESSAGE_TYPES,
  NUDGYT_AGENT_ACTIVITY_STATUSES,
  VAD_STATES,
  MULTI_CHARACTER_STOPPABLE_RESPONSE_TYPES,
  STOP_RESPONSE_GATE_FALLBACK_MS
} from '../../constants/simulationConstants';
import { isIOS, postMessageUserText, postMessage } from '../../helpers/utils';
import { buildUserEmotionWsField } from '../../helpers/userEmotion';
import { buildAudioQueryPayload } from '../../helpers/liveChatAudio';
import { useSimulationContext } from '../../providers/SimulationProvider';
import useAudioQueue from '../useAudioQueue';
import useCoreLiveChat from '../useCoreLiveChat';
import useRequestLiveChatUserEmotion from '../useRequestLiveChatUserEmotion';
import useResponseTimeout from '../useResponseTimeout';
import useCoreLiveChatListener from '../useCoreLiveChatListener';
import useCoreRequestedUserEmotionListener from '../useCoreRequestedUserEmotionListener';
import useCoreRequestUserEmotionInterval from '../useCoreRequestUserEmotionInterval';
import useMultiCharacterSpeakerState from './useMultiCharacterSpeakerState';
import nudgytAgentConfig from '../../config/nudgytAgentConfig';
import createNudgytAgentChatSocket from '../../websocket/nudgytAgentChatSocket';

/**
 * Nudgyt Agent client for a multi-character Basic + Nudgyt Core simulation on
 * 3JS avatars. Single-character Core uses {@link useThreeNudgytAgentCoreClient}.
 *
 * All the characters share one agent session and one socket, authenticated by
 * the session token alone. The agent's router picks who answers each learner
 * turn and announces it with `speaker_started` / `speaker_ended`; every text,
 * emotion and audio event carries the speaking `bot_id`, which routes it to
 * that character's avatar. Everything except the `text_query` echo is
 * forwarded to the parent, `bot_id` included, so it can attribute the
 * transcript.
 */
function useThreeNudgytAgentMultiCharacterCoreClient() {
  const {
    talkingBotId,
    emotionsByBotId,
    setTalkingBotId,
    registerSessionAgents,
    startSpeaker,
    endSpeaker,
    resolveSpeakerBotId,
    setSpeakerEmotion
  } = useMultiCharacterSpeakerState();

  const { audioRef, enqueueAudio, clearAudioQueue } = useAudioQueue({
    onItemPlay: (item) => setTalkingBotId(item?.botId ?? null)
  });

  const [isWsConnected, setIsWsConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const shouldReconnectRef = useRef(true);
  const isIntentionalCloseRef = useRef(false);
  const aiResponseInFlightRef = useRef(false);
  const [isAiResponseInFlight, setIsAiResponseInFlightState] = useState(false);
  const setAiResponseInFlight = useCallback((inFlight) => {
    aiResponseInFlightRef.current = inFlight;
    setIsAiResponseInFlightState(inFlight);
  }, []);
  const startResponseTimeoutRef = useRef(null);
  const clearResponseTimeoutRef = useRef(null);
  const lastAudioQueryUserEmotionRef = useRef(null);
  const lastTextQueryUserEmotionRef = useRef(null);
  /**
   * Set while a stop is pending the server's acknowledgement. Content chunks
   * of the stopped reply are dropped rather than played or forwarded.
   */
  const stoppedResponseRef = useRef(false);
  const stopResponseFallbackTimeoutRef = useRef(null);

  const requestLiveChatUserEmotion = useRequestLiveChatUserEmotion();

  const {
    botIds,
    userText,
    isAiTalking,
    facialData,
    documentIds,
    textQueryUserEmotion,
    userEmotion,
    userInteract,
    isSceneReady,
    isAvatarHidden,
    nudgytSessionToken,
    isClientInitialized,
    isEmotionRecognitionEnabled,
    isLiveChatActive,
    isStopResponseRequested,
    setUserText,
    setIsAiTalking,
    setFacialData,
    setIsClientInitialized,
    setDocumentIds,
    setIsStopResponseRequested,
    setTextQueryUserEmotion,
    setUserInteract
  } = useSimulationContext();

  const hasSession = Boolean(nudgytSessionToken);

  const isAiTalkingGuardRef = useRef(isAiTalking);
  isAiTalkingGuardRef.current = isAiTalking;

  const clearStoppedResponseGate = useCallback(() => {
    stoppedResponseRef.current = false;
    if (stopResponseFallbackTimeoutRef.current) {
      clearTimeout(stopResponseFallbackTimeoutRef.current);
      stopResponseFallbackTimeoutRef.current = null;
    }
  }, []);

  const resetClientState = useCallback(() => {
    setIsAiTalking(false);
    setUserText('');
    setFacialData([]);
    setDocumentIds([]);
  }, [setDocumentIds, setFacialData, setIsAiTalking, setUserText]);

  const sendAudioQuery = useCallback(
    async (base64Audio, format) => {
      if (wsRef.current?.readyState !== WebSocket.OPEN) return false;

      const userEmotion = await requestLiveChatUserEmotion();
      lastAudioQueryUserEmotionRef.current = userEmotion;

      wsRef.current.send(
        JSON.stringify(
          buildAudioQueryPayload({
            base64Audio,
            format,
            userEmotion
          })
        )
      );
      startResponseTimeoutRef.current?.();
      return true;
    },
    [requestLiveChatUserEmotion]
  );

  // Above useResponseTimeout, whose onTimeout needs these; the timeout
  // functions are reached through the refs assigned below.
  const { postLiveChatStatus, startListening } = useCoreLiveChat({
    sendAudioQuery,
    onUserBargeIn: clearAudioQueue,
    clearResponseTimeout: () => clearResponseTimeoutRef.current?.()
  });

  const { startResponseTimeout, clearResponseTimeout } = useResponseTimeout({
    onTimeout: () => {
      resetClientState();
      setAiResponseInFlight(false);
      postLiveChatStatus(VAD_STATES.IDLE);
      startListening();
    }
  });

  useEffect(() => {
    startResponseTimeoutRef.current = startResponseTimeout;
    clearResponseTimeoutRef.current = clearResponseTimeout;
  }, [clearResponseTimeout, startResponseTimeout]);

  useCoreLiveChatListener();
  useCoreRequestedUserEmotionListener(isEmotionRecognitionEnabled);
  useCoreRequestUserEmotionInterval({
    isClientInitialized,
    isAiTalking,
    isAiResponseInFlight
  });

  const handleAgentMessage = useCallback(
    (aiMessage) => {
      switch (aiMessage.type) {
        case NUDGYT_AGENT_MESSAGE_TYPES.CONNECTED:
          registerSessionAgents(aiMessage.session_agents, botIds);
          break;
        case NUDGYT_AGENT_MESSAGE_TYPES.TEXT_QUERY:
          startResponseTimeout();
          postMessageUserText({
            text: aiMessage.text,
            userEmotion:
              textQueryUserEmotion ??
              lastTextQueryUserEmotionRef.current ??
              lastAudioQueryUserEmotionRef.current
          });
          lastTextQueryUserEmotionRef.current = null;
          break;
        case NUDGYT_AGENT_MESSAGE_TYPES.RESPONSE_START:
          resetClientState();
          clearResponseTimeout();
          break;
        case NUDGYT_AGENT_MESSAGE_TYPES.SPEAKER_STARTED:
          startSpeaker(aiMessage.bot_id);
          break;
        case NUDGYT_AGENT_MESSAGE_TYPES.SPEAKER_ENDED:
          endSpeaker(aiMessage.bot_id);
          break;
        case NUDGYT_AGENT_MESSAGE_TYPES.EMOTION:
          setSpeakerEmotion(
            resolveSpeakerBotId(aiMessage.bot_id),
            aiMessage.emotion
          );
          break;
        case NUDGYT_AGENT_MESSAGE_TYPES.AUDIO_RESPONSE:
          enqueueAudio({
            audio: aiMessage.audio,
            viseme: aiMessage.viseme || [],
            botId: resolveSpeakerBotId(aiMessage.bot_id)
          });
          break;
        // `activity_status` / busy is handled in ws.onmessage instead: it has
        // to clear the refused turn's timeout even while the stop gate drops
        // the frame, and the gate sits above this switch.
        //
        // Fallback unmute: a short or errored reply can skip the isAiTalking
        // cycle that normally reopens the mic.
        case NUDGYT_AGENT_MESSAGE_TYPES.RESPONSE_ENDED:
        case NUDGYT_AGENT_MESSAGE_TYPES.INTRO_SKIPPED:
          startListening({ onlyUnmute: true });
          break;
        default:
      }
    },
    [
      botIds,
      clearResponseTimeout,
      endSpeaker,
      enqueueAudio,
      registerSessionAgents,
      resetClientState,
      resolveSpeakerBotId,
      setSpeakerEmotion,
      startListening,
      startResponseTimeout,
      startSpeaker,
      textQueryUserEmotion
    ]
  );

  const onMessageRef = useRef(handleAgentMessage);
  onMessageRef.current = handleAgentMessage;

  const closeLiveSocket = (reason = 'Closing connection') => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    const socket = wsRef.current;
    if (!socket) return;

    isIntentionalCloseRef.current = true;
    if (
      socket.readyState === WebSocket.OPEN ||
      socket.readyState === WebSocket.CONNECTING
    ) {
      socket.close(1000, reason);
    }
    wsRef.current = null;
    setIsWsConnected(false);
  };

  const scheduleReconnect = () => {
    reconnectAttemptsRef.current += 1;
    reconnectTimeoutRef.current = setTimeout(() => {
      connectWebSocket();
    }, nudgytAgentConfig.reconnectDelay);
  };

  const connectWebSocket = () => {
    if (!hasSession) {
      console.error(
        '[Nudgyt Agent Multi-Character Client] Missing auth: nudgytSessionToken'
      );
      return;
    }

    closeLiveSocket('Replacing connection');

    const ws = createNudgytAgentChatSocket({
      base: nudgytAgentConfig.baseUrl,
      sessionToken: nudgytSessionToken
    });

    if (!ws) return;

    ws.onopen = () => {
      if (wsRef.current !== ws) {
        isIntentionalCloseRef.current = true;
        ws.close(1000, 'Superseded by newer connection');
        isIntentionalCloseRef.current = false;
        return;
      }

      setIsWsConnected(true);
      reconnectAttemptsRef.current = 0;
      isIntentionalCloseRef.current = false;
    };

    ws.onmessage = (ev) => {
      let aiMessage;
      try {
        aiMessage = JSON.parse(ev.data);
      } catch {
        return;
      }

      const isGatedTextToken =
        !aiResponseInFlightRef.current &&
        aiMessage.type === NUDGYT_AGENT_MESSAGE_TYPES.TEXT_TOKEN;

      if (isGatedTextToken) {
        return;
      }

      // A turn the server refused because another reply is already running.
      // `activity_status` is a stoppable type, so the gate below would drop it
      // and the refused turn's response timeout would be left to expire — long
      // after the stop — resetting client state and reopening the mic
      // mid-reply. The frame is still gated for the parent: only the timer goes.
      if (
        aiMessage.type === NUDGYT_AGENT_MESSAGE_TYPES.ACTIVITY_STATUS &&
        aiMessage.status === NUDGYT_AGENT_ACTIVITY_STATUSES.BUSY
      ) {
        clearResponseTimeoutRef.current?.();
      }

      // Chunks still arriving from a reply the learner stopped. Returning here
      // is before the postMessage forward below, so nothing reaches the parent
      // either.
      if (
        stoppedResponseRef.current &&
        MULTI_CHARACTER_STOPPABLE_RESPONSE_TYPES.includes(aiMessage.type)
      ) {
        return;
      }

      switch (aiMessage.type) {
        case NUDGYT_AGENT_MESSAGE_TYPES.RESPONSE_START:
          setAiResponseInFlight(true);
          // A reply that starts after a stop is a new one; this reopens the
          // gate when the agent skips the stop acknowledgement.
          clearStoppedResponseGate();
          break;
        case NUDGYT_AGENT_MESSAGE_TYPES.SPEAKER_STARTED:
        case NUDGYT_AGENT_MESSAGE_TYPES.TEXT_START:
        case NUDGYT_AGENT_MESSAGE_TYPES.EMOTION:
          setAiResponseInFlight(true);
          break;
        case NUDGYT_AGENT_MESSAGE_TYPES.RESPONSE_ENDED:
        case NUDGYT_AGENT_MESSAGE_TYPES.INTRO_SKIPPED:
          setAiResponseInFlight(false);
          break;
        case NUDGYT_AGENT_MESSAGE_TYPES.ERROR:
        case NUDGYT_AGENT_MESSAGE_TYPES.CANCELLED:
        case NUDGYT_AGENT_MESSAGE_TYPES.RESPONSE_STOPPED:
          setAiResponseInFlight(false);
          clearStoppedResponseGate();
          break;
        default:
      }

      onMessageRef.current?.(aiMessage);

      if (aiMessage.type !== NUDGYT_AGENT_MESSAGE_TYPES.TEXT_QUERY) {
        postMessage({
          type: MESSAGE_TYPES.CHAT,
          payload: { from: MESSAGE_FROM.AI, ...aiMessage }
        });
      }
    };

    ws.onerror = (error) => {
      console.error(
        '[Nudgyt Agent Multi-Character Client] WebSocket error:',
        error
      );
      clearResponseTimeout();
    };

    ws.onclose = (event) => {
      if (event.target !== wsRef.current) {
        isIntentionalCloseRef.current = false;
        return;
      }

      setIsWsConnected(false);
      wsRef.current = null;

      if (
        !isIntentionalCloseRef.current &&
        shouldReconnectRef.current &&
        reconnectAttemptsRef.current < nudgytAgentConfig.maxReconnectAttempts
      ) {
        scheduleReconnect();
      } else if (
        reconnectAttemptsRef.current >= nudgytAgentConfig.maxReconnectAttempts
      ) {
        postMessage({
          type: MESSAGE_TYPES.CHAT,
          error: 'Connection failed'
        });
      }

      isIntentionalCloseRef.current = false;
    };

    wsRef.current = ws;
  };

  useEffect(() => {
    if (!hasSession) {
      closeLiveSocket('Missing auth');
      return undefined;
    }

    shouldReconnectRef.current = true;
    reconnectAttemptsRef.current = 0;
    connectWebSocket();

    return () => {
      shouldReconnectRef.current = false;
      closeLiveSocket('Component unmounting');
      setAiResponseInFlight(false);
      clearStoppedResponseGate();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nudgytSessionToken]);

  useEffect(() => {
    if (!hasSession) return;

    const onParentMessage = (event) => {
      let data = event.data;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch {
          return;
        }
      }
      if (
        data?.type === MESSAGE_TYPES.CHAT &&
        data?.payload?.type === NUDGYT_AGENT_MESSAGE_TYPES.STOP_AUDIO
      ) {
        clearAudioQueue();
        resetClientState();
      }
    };

    window.addEventListener('message', onParentMessage);
    return () => window.removeEventListener('message', onParentMessage);
  }, [hasSession, clearAudioQueue, resetClientState]);

  useEffect(() => {
    if (!hasSession || !isStopResponseRequested) return;

    clearResponseTimeout();
    resetClientState();
    // The reply is being abandoned, so everything queued for it goes with it.
    clearAudioQueue();
    setAiResponseInFlight(false);

    stoppedResponseRef.current = true;
    // Safety valve: an agent that never acknowledges the stop would otherwise
    // leave the gate shut and mute every later reply.
    if (stopResponseFallbackTimeoutRef.current)
      clearTimeout(stopResponseFallbackTimeoutRef.current);
    stopResponseFallbackTimeoutRef.current = setTimeout(() => {
      stopResponseFallbackTimeoutRef.current = null;
      stoppedResponseRef.current = false;
    }, STOP_RESPONSE_GATE_FALLBACK_MS);

    setIsStopResponseRequested(false);

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({ type: NUDGYT_AGENT_MESSAGE_TYPES.STOP_RESPONSE })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStopResponseRequested, hasSession]);

  useEffect(() => {
    if (!hasSession) return;
    if (!isWsConnected || !wsRef.current) return;
    if (!userText?.trim() && !(documentIds?.length > 0)) return;

    setAiResponseInFlight(true);
    clearAudioQueue();
    const userEmotionField = buildUserEmotionWsField(textQueryUserEmotion);

    const textQueryPayload = {
      type: NUDGYT_AGENT_MESSAGE_TYPES.TEXT_QUERY,
      ...(userText?.trim() && { text: userText }),
      ...(documentIds?.length > 0 && { document_ids: documentIds }),
      local_time: new Date().toISOString().slice(0, 19).replace('T', ' '),
      mute_audio: false,
      ...(userEmotionField ?? {})
    };
    lastTextQueryUserEmotionRef.current = textQueryUserEmotion;

    wsRef.current.send(JSON.stringify(textQueryPayload));
    startResponseTimeout();
    setUserText('');
    setDocumentIds([]);
    setTextQueryUserEmotion(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSession, userText, documentIds, isWsConnected, textQueryUserEmotion]);

  useEffect(() => {
    if (!hasSession) return;
    if (!userInteract || !isWsConnected || !wsRef.current) return;

    clearAudioQueue();
    wsRef.current.send(JSON.stringify(userInteract));
    setUserInteract(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSession, userInteract, isWsConnected]);

  useEffect(() => {
    if (
      isWsConnected &&
      !isClientInitialized &&
      (isSceneReady || isAvatarHidden)
    ) {
      setIsClientInitialized(true);
      if (!isIOS()) {
        postMessage({ type: MESSAGE_TYPES.INITIALIZED });
      }
    }
  }, [
    isAvatarHidden,
    isClientInitialized,
    isSceneReady,
    isWsConnected,
    setIsClientInitialized
  ]);

  useEffect(() => {
    if (!isWsConnected || !wsRef.current || !isEmotionRecognitionEnabled)
      return;
    if (aiResponseInFlightRef.current || isAiTalkingGuardRef.current) return;
    if (isLiveChatActive) return;

    const userEmotionField = buildUserEmotionWsField(userEmotion);
    if (!userEmotionField) return;

    const emotionPayload = {
      type: NUDGYT_AGENT_MESSAGE_TYPES.TEXT_QUERY,
      ...userEmotionField
    };

    wsRef.current.send(JSON.stringify(emotionPayload));

    postMessage({
      type: MESSAGE_TYPES.CHAT,
      payload: {
        from: MESSAGE_FROM.USER,
        type: NUDGYT_AGENT_MESSAGE_TYPES.USER_EMOTION,
        emotion: userEmotionField.user_emotion
      }
    });
  }, [
    isEmotionRecognitionEnabled,
    isLiveChatActive,
    isWsConnected,
    userEmotion
  ]);

  return {
    client: {
      isNudgytAgentClient: true,
      audioRef,
      isTalking: isAiTalking,
      facialData,
      setUserText,
      setIsAiTalking
    },
    talkingBotId,
    emotionsByBotId
  };
}

export default useThreeNudgytAgentMultiCharacterCoreClient;
